import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { FiChevronRight, FiFileText } from 'react-icons/fi';
import { db } from '../../lib/firebase';
import { allows } from '../../lib/permissions';
import { formatDate } from '../../lib/format';
import { normalizeLinks } from '../../lib/links';
import { storedDocuments } from '../../lib/documents';
import { Notice, Panel, Pill, SkeletonRows } from '../ui/kit';
import { splitList } from '../patients/patientModel';
import DocumentLinks from '../documents/DocumentLinks';
import DocumentViewer from '../documents/DocumentViewer';
import { croAccess } from './croModel';

const LINK = 'inline-flex items-center gap-1 text-[13px] font-medium text-brand-600 no-underline transition hover:text-brand-700';

// Operative reports attached to this patient file.
const PatientCros = ({ patient, user, rights }) => {
  const [state, setState] = useState({ status: 'loading', items: [] });
  const [viewing, setViewing] = useState(null); // { item, cro }

  useEffect(() => {
    let cancelled = false;
    getDocs(query(collection(db, 'cros'), where('patientId', '==', patient._id)))
      .then((snapshot) => {
        const items = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => new Date(b.datePatient || b.createdAt || 0) - new Date(a.datePatient || a.createdAt || 0));
        if (!cancelled) setState({ status: 'ready', items });
      })
      .catch(() => !cancelled && setState({ status: 'error', items: [] }));
    return () => {
      cancelled = true;
    };
  }, [patient._id]);

  return (
    <Panel
      title="Comptes rendus opératoires"
      action={
        <Link to="/cro" className={`${LINK} print:hidden`}>
          Section CRO <FiChevronRight size={13} aria-hidden="true" />
        </Link>
      }
    >
      {state.status === 'loading' ? (
        <SkeletonRows rows={1} />
      ) : state.status === 'error' ? (
        <Notice tone="amber">Comptes rendus indisponibles pour le moment.</Notice>
      ) : state.items.length === 0 ? (
        <p className="m-0 text-[13.5px] text-black/45">Aucun compte rendu rattaché à ce patient.</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-[12px] p-0">
          {state.items.map((cro) => {
            // The texts are the reference (the "selected…" lists repeat the same names).
            const surgeons = splitList(cro.chirurgiens);
            const diagnostics = splitList([cro.indicationOperatoire, cro.diagnos].filter(Boolean).join(','));
            const links = normalizeLinks(cro.dropboxLinks);
            const documents = storedDocuments(cro.documents);
            return (
              <li key={cro.id} className="flex gap-[12px] rounded-2xl bg-mist px-[14px] py-[12px]">
                <span className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-white text-orange-500">
                  <FiFileText size={15} aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Link to={`/cro/${cro.id}`} className="text-[14px] font-semibold text-ink no-underline hover:text-brand-700">
                      {formatDate(cro.datePatient) || 'Date non précisée'}
                    </Link>
                    {cro.statut && <Pill tone={cro.statut === 'Validé' ? 'green' : 'amber'}>{cro.statut}</Pill>}
                  </div>
                  {surgeons.length > 0 && <p className="mb-0 mt-1 text-[13px] text-black/55">{surgeons.join(', ')}</p>}
                  {diagnostics.length > 0 && <p className="mb-0 mt-0.5 text-[13px] text-black/45">{diagnostics.join(' · ')}</p>}
                  {(documents.length > 0 || links.length > 0) && (
                    <div className="mt-2">
                      <DocumentLinks documents={documents} links={links} onOpen={(item) => setViewing({ item, cro })} size="sm" />
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {viewing && (
        <DocumentViewer
          item={viewing.item}
          onClose={() => setViewing(null)}
          canReplace={croAccess(user).canEdit(viewing.cro)}
          canDownload={allows(rights, 'cro', 'tout')}
        />
      )}
    </Panel>
  );
};

export default PatientCros;
